'use client'

import { useCallback, useEffect, useState, useRef } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import SectionHeader from '@/components/ui/SectionHeader'
import { ChevronLeft, ChevronRight, Star } from 'lucide-react'

interface Review {
  id?: string | null
  authorName: string
  rating: number
  text: string
}

interface HeaderGroup {
  infoTagText?: string | null
  title?: string | null
  subtitle?: string | null
}

interface CarouselSettings {
  enableAutoAdvance?: boolean | null
  autoAdvanceInterval?: number | null
}

interface RezensionenClientProps {
  header?: HeaderGroup | null
  reviews: Review[]
  carouselSettings?: CarouselSettings | null
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${i < rating ? 'fill-brand text-brand' : 'fill-none text-brand/20'}`}
        />
      ))}
    </div>
  )
}

export default function RezensionenClient({
  header,
  reviews,
  carouselSettings,
}: RezensionenClientProps) {
  const [emblaRef, embla] = useEmblaCarousel({ align: 'start', loop: true })
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const onSelect = useCallback(() => {
    if (!embla) return
    setCanLeft(embla.canScrollPrev())
    setCanRight(embla.canScrollNext())
  }, [embla])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on('select', onSelect)
    embla.on('reInit', onSelect)
    return () => {
      embla.off('select', onSelect)
      embla.off('reInit', onSelect)
    }
  }, [embla, onSelect])

  // Auto-advance logic with pause on hover
  useEffect(() => {
    if (!embla || !carouselSettings?.enableAutoAdvance || isHovered) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    intervalRef.current = setInterval(() => {
      embla.scrollNext()
    }, carouselSettings.autoAdvanceInterval || 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [embla, carouselSettings, isHovered])

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla])
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla])

  return (
    <div>
      {header && (header.infoTagText || header.title || header.subtitle) && (
        <SectionHeader
          infoTagText={header.infoTagText || ''}
          title={header.title || ''}
          size="md"
          subtitle={header.subtitle || undefined}
          align="left"
        />
      )}

      <div
        className="space-y-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6 items-stretch">
            {reviews.map((review, index) => (
              <div
                key={review.id || index}
                className="flex-none basis-[85%] sm:basis-[48%] lg:basis-1/3 min-w-0 flex"
              >
                <div className="bg-brand-surface-1 px-8 py-10 flex flex-col h-full w-full">
                  <StarRating rating={review.rating} />
                  <p className="mt-4 body-base text-secondary flex-1">{review.text}</p>
                  <p className="mt-4 title-sm text-primary font-bold">{review.authorName}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canLeft}
            className="inline-flex items-center gap-2 bg-brand text-text-white px-6 py-3 font-metropolis disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>ZURÜCK</span>
          </button>
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canRight}
            className="inline-flex items-center gap-2 bg-brand text-text-white px-6 py-3 font-metropolis disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span>WEITER</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
